import { DrawerProps, Tag, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useForm } from 'react-hook-form';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { PhoneInput } from '@dynamika-shared/ui/PhoneInput/PhoneInput';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { NoDataSelect } from '@dynamika-shared/ui/NoDataSelect/NoDataSelect';
import { User, UserRequest } from '@dynamika-shared/types/users';
import { UserRoles } from '@dynamika-shared/types/auth';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { Switch } from '@dynamika-shared/ui/Switch/Switch';
import { mapRolesToOptions } from './roleOptions';
import {
  filteredSupportedUiRoles,
  supportedUiRoles,
} from 'src/constants/rolesList';
import { userValidationSchema } from './userValidationSchema';
import { formatFormErrors } from '@utils/formatFormErrors';
import { InstitutionInfo } from '@components/InstitutionInfo/InstitutionInfo';
import { UsersListPageViewProps } from '../UsersList/UsersList.view';

import { WithConditionalWrapperHoc } from '@components/HOC/WithConditionalWrapper/WithConditionalWrapper.hoc';
import { SelectedTags } from '@components/SelectedTags/SelectedTags';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { DropdownOptionType } from '../UsersList/UsersTable.columns';
import { useOrganizationsSlice } from '@restore/organizations/organizations.slice';
import {
  doUserFailure,
  doUserSuccess,
  useUsersListSlice,
} from '@restore/usersList/usersList.slice';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { subscribeToActions } from '@restore/helpers';
import { Option } from '@utils/generateOptions';
import {
  convertUserToUserRequest,
  countryOptions,
  filterOptionHandler,
} from './utilsCreateUser';
import { runToast } from '@restore/tools';

type CreateUserProps = DrawerProps &
  Pick<UsersListPageViewProps, 'refetchUsers'> & {
    close?: () => void;
    mode: DropdownOptionType;
    currentUser?: User;
  };

export const CreateUser: FC<CreateUserProps> = ({
  close,
  currentUser,
  mode,
  refetchUsers,
}) => {
  const { getValues, setValue, trigger, watch, formState, setError } = useForm({
    resolver: userValidationSchema,
    defaultValues: currentUser
      ? convertUserToUserRequest(currentUser)
      : undefined,
  });

  // ======================== STATE ========================
  const [isAskQuitOPen, setIsAskQuitOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [searchInstitutionValue, setSearchInstitutionValue] = useState('');
  const [searchCountryValue, setSearchCountryValue] = useState('');
  const userData = useAuthSlice(s => s.data);
  const [institutionsAll, fetchAllOrganizations] = useOrganizationsSlice(
    (state, actions) => [state.institutionsAll, actions.fetchAllOrganizations]
  );
  const [doUserAction, userActionLoading, error] = useUsersListSlice(
    (state, actions) => [actions.doUser, state.userActionLoading, state.error]
  );
  const [selectedRoles, setSelectedRoles] = useState<Option[]>(
    currentUser ? mapRolesToOptions(getValues().roles) : []
  );

  // ======================== VALUES ========================
  const { isDirty, isValid, errors, touchedFields } = formState;

  const isUserAdmin = userData?.selectedRole === UserRoles.ADMIN;
  const isAdminRoleDisabled = useMemo(
    () =>
      userData?.selectedRole !== UserRoles.ADMIN ||
      !selectedRoles.some(role => role.value === UserRoles.PROJECT_MANAGER),
    [selectedRoles, userData?.selectedRole]
  );

  const isPM = useAuthSlice(
    s => s?.data?.selectedRole === UserRoles.PROJECT_MANAGER
  );
  const uiRoles = isPM ? filteredSupportedUiRoles : supportedUiRoles;

  const isSubmitDisabled =
    !isValid || isSubmitting || !isDirty || userActionLoading;
  const isAdminChecked = getValues().roles?.includes(UserRoles.ADMIN);

  const submitButtonName =
    mode === DropdownOptionType.create ? 'Create' : 'Update';

  const drawerTitle = `${submitButtonName} User`;

  const institutionsOptions = useMemo(() => {
    return institutionsAll.map(i => ({
      label: (
        <InstitutionInfo
          name={i.name}
          address={i.address}
          classNameText='h-5'
        />
      ),
      value: i.id,
    }));
  }, [institutionsAll]);

  // ======================== HANDLERS ========================
  const onChangeField = (
    fieldName: keyof UserRequest,
    value: string | UserRoles[]
  ) => {
    setValue(fieldName, value, { shouldDirty: true, shouldTouch: true });
    trigger(fieldName);
  };
  const onChangeItems = (items: Option[]) => {
    const newItems = items.map(label => label.value);
    setValue('roles', newItems as UserRoles[], {
      shouldDirty: true,
      shouldTouch: true,
    });
    trigger('roles');
  };

  const onBlur = (fieldName: keyof UserRequest, value: string) => {
    if (touchedFields[fieldName]) {
      onChangeField(fieldName, value.trim());
    }
  };

  const onSelect = (value: string) => {
    const selectedItem = uiRoles.find(f => f.value === value);
    if (selectedItem) {
      setSelectedRoles(prevState => {
        const updatedItems = [...prevState, selectedItem];
        onChangeItems(updatedItems);
        return updatedItems;
      });
    }
  };

  const onDeselect = (value: string) => {
    setSelectedRoles(prevState => {
      const updatedItems = prevState.filter(item => item.value !== value);
      if (value === UserRoles.PROJECT_MANAGER) {
        const indexToRemove = updatedItems.findIndex(
          item => item.value === UserRoles.ADMIN
        );
        if (indexToRemove !== -1) {
          updatedItems.splice(indexToRemove, 1);
        }
        onChangeField(
          'roles',
          updatedItems.map(({ value }) => value as UserRoles)
        );
      }

      onChangeItems(updatedItems);
      return updatedItems;
    });
  };

  const submit = () => {
    setIsSubmitting(true);
    const userData = getValues() as UserRequest;
    const userRequest = {
      ...userData,
      ...(userData.phone && {
        phone: userData.phone.replace(/[()\s-]/g, ''),
      }),
    };
    if (mode === DropdownOptionType.create) {
      doUserAction({
        userRequest,
        actionName: 'createUser',
      });
    }
    if (mode === DropdownOptionType.edit && currentUser?.id) {
      doUserAction({
        userRequest,
        userId: currentUser?.id,
        actionName: 'updateUser',
      });
    }
    subscribeToActions(doUserFailure, () => {
      setIsSubmitting(false);
    });
    subscribeToActions(doUserSuccess, () => {
      close && close();
      refetchUsers();
    });
  };

  const handleTagClose = (removedItem: Option) => {
    if (isPM && removedItem.value === 'SUPPORT') {
      runToast(
        'error',
        'As a Project Manager, you are not allowed to remove the SUPPORT role.'
      );
      return;
    }

    onDeselect(removedItem.value);
  };

  const onCheckAdmin = (checked: boolean) => {
    const { roles } = getValues();
    const newRoles = checked
      ? [...roles, UserRoles.ADMIN]
      : roles.filter(role => role !== UserRoles.ADMIN);
    onChangeField('roles', newRoles as UserRoles[]);
    setSelectedRoles(mapRolesToOptions(newRoles));
  };

  const onCloseDrawer = () => {
    if (!isDirty) {
      close && close();
    } else {
      setIsAskQuitOpen(true);
    }
  };

  // ======================== EFFECTS ========================
  useEffect(() => {
    fetchAllOrganizations({});
  }, []);

  useEffect(() => {
    const message = error?.error?.response?.data?.detail;
    const { email } = getValues();
    if (error?.actionName === 'createUser' && message?.includes(email)) {
      setError('email', {
        type: 'manual',
        message,
      });
    }
  }, [error]);

  return (
    <Drawer
      classNames={{
        header:
          '!px-0 !pb-3 !pt-1 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !px-0 !pb-0',
        content: 'rounded-lg',
      }}
      title={drawerTitle}
      open
      width={464}
      placement='right'
      onClose={onCloseDrawer}
      closeIcon={<CloseOutlined />}
      data-testid={'createUserDrawer'}
    >
      <form className='overflow-auto'>
        <div className='flex w-full flex-col gap-3 pr-2'>
          <Input
            required
            minLength={1}
            maxLength={35}
            value={watch('firstName')}
            name='firstName'
            data-testid='firstName'
            onChange={e => onChangeField('firstName', e.target.value)}
            onBlur={e => onBlur('firstName', e.target.value)}
            errors={formatFormErrors(errors.firstName?.message)}
            label={'First name'}
            placeholder={'First name'}
          />
          <Input
            required
            minLength={1}
            maxLength={35}
            value={watch('lastName')}
            name='lastName'
            data-testid='lastName'
            onChange={e => onChangeField('lastName', e.target.value)}
            onBlur={e => onBlur('lastName', e.target.value)}
            errors={formatFormErrors(errors.lastName?.message)}
            label={'Last name'}
            placeholder={'Last name'}
          />
          <Input
            required
            maxLength={50}
            value={watch('email')}
            name='email'
            data-testid='email'
            onChange={e => onChangeField('email', e.target.value)}
            onBlur={e => onBlur('email', e.target.value)}
            errors={formatFormErrors(errors.email?.message)}
            label={'Email'}
            placeholder={'Email'}
            disabled={mode === DropdownOptionType.edit}
            disabledOpacity={100}
          />
          <PhoneInput
            value={watch('phone') as string}
            data-testid='phone'
            onChange={phone => onChangeField('phone', phone)}
            onBlur={e => onBlur('phone', e.target.value)}
            errors={errors.phone?.message}
            label={'Phone number'}
            placeholder={'Enter phone number'}
          />
          <Select
            showSearch
            listHeight={160}
            onSearch={setSearchInstitutionValue}
            filterOption={useCallback(
              filterOptionHandler('label.props.name', setSearchCountryValue),
              []
            )}
            notFoundContent={
              <NotFoundContent
                value={searchInstitutionValue}
                name={'Institution'}
              />
            }
            errors={errors.organizationId?.message}
            options={institutionsOptions}
            value={watch('organizationId')}
            name='organizationId'
            onChange={e => onChangeField('organizationId', e)}
            label={'Institution'}
            placeholder={'Institution'}
            data-testid='organizationId'
            required
          />
          <Select
            showSearch
            filterOption={useCallback(
              filterOptionHandler('label', setSearchCountryValue),
              []
            )}
            listHeight={160}
            errors={errors.country?.message}
            options={countryOptions}
            value={watch('country')}
            notFoundContent={
              <NotFoundContent value={searchCountryValue} name={'country'} />
            }
            name='country'
            onChange={e => onChangeField('country', e)}
            label={'Country'}
            placeholder={'Select a country'}
            data-testid='country'
          />
          <div className='mb-2'>
            <Select
              required
              mode='multiple'
              tagRender={tagRenderer}
              filterOption={false}
              listHeight={160}
              showSearch={false}
              errors={errors.roles?.message}
              options={uiRoles}
              value={watch('roles')}
              name='roles'
              onSelect={onSelect}
              onDeselect={onDeselect}
              onChange={e => onChangeField('roles', [e])}
              label={'Role Group'}
              placeholder={'Select Role Group'}
              data-testid='roles'
            />
            <SelectedTags
              selectedItems={selectedRoles.filter(
                r => r.value !== UserRoles.ADMIN
              )}
              onClose={handleTagClose}
            />
          </div>
          {isUserAdmin && (
            <WithConditionalWrapperHoc
              useWrapper={isAdminRoleDisabled}
              wrapper={
                <Tooltip
                  color='white'
                  title={
                    <span className='text-1424 text-black'>
                      Full unrestricted admin permissions can be given to
                      accounts only with the Project Manager role
                    </span>
                  }
                />
              }
            >
              <Switch
                disabled={isAdminRoleDisabled}
                inOneLine
                classNameLabel={'mb-2'}
                classNameInputWrapper={'grow-0'}
                defaultChecked={isAdminChecked}
                checked={isAdminChecked}
                onChange={onCheckAdmin}
                name={'admin'}
                label={'Full Unrestricted Admin'}
                size={'small'}
              />
            </WithConditionalWrapperHoc>
          )}
        </div>
      </form>
      <footer className='mt-auto'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button className='w-[17rem]' onClick={onCloseDrawer}>
            Cancel
          </Button>
          <Button
            className='w-[17rem]'
            type='primary'
            disabled={isSubmitDisabled}
            onClick={submit}
          >
            {submitButtonName}
          </Button>
        </div>
      </footer>
      <UnsavedChangesModal
        open={isAskQuitOPen}
        onConfirm={close}
        onCancel={() => setIsAskQuitOpen(false)}
      />
    </Drawer>
  );
};

function tagRenderer() {
  return <Tag className='hidden' />;
}

function NotFoundContent({ value, name }: { value: string; name: string }) {
  return value ? (
    <NoDataSelect text={value} maxLength={10} name={name} />
  ) : null;
}
